<template>
  <property-report-card
    title="Estimated Rent Roll"
    description="View estimated rents, availability, and time on market by floorplan for your property and comps."
    :printing-mode="printingMode"
    :is-empty="rows.length === 0"
  >
    <template #actions>
      <q-btn no-caps flat>
        <div class="row items-center">
          <q-icon name="download" class="q-mr-sm" size="medium" />Download
        </div>
        <q-menu>
          <q-list separator>
            <q-item clickable v-close-popup @click="exportCSV(false)">
              <q-item-section> Floorplan Averages </q-item-section>
            </q-item>
            <q-item clickable v-close-popup @click="exportCSV(true)">
              <q-item-section> Unit Details </q-item-section>
            </q-item>
          </q-list>
        </q-menu>
      </q-btn>
      <select-options
        v-model="propertySelections"
        :options="buildingOptions"
        label="Properties"
        no-empty
        @update:model-value="(val) => $tracker.changedFilter('properties', 'unit_mix', val)"
      />
      <q-select
        v-model="groupBySelection"
        transition-duration="100"
        class="overflow-select"
        style="width: 150px"
        dense
        outlined
        label="Group By"
        :options="['Type', 'Floorplan', 'Bed/Bath/SF']"
        @update:model-value="(value) => $tracker.changedFilter('metric', 'unit_mix', value.value)"
      />
    </template>
    <q-table
      id="intercom-estimated-unit-mix-content"
      bordered
      dense
      hide-bottom
      row-key="id"
      :rows="rows"
      :columns="columns"
      :rows-per-page-options="[0]"
      :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
    >
      <template #header-cell="props">
        <q-th :props="props" @click="sortingChanged(props.col.field)">
          {{ props.col.label }}
          <q-tooltip v-if="props.col.description" max-width="250px">
            <b>{{ props.col.label }}</b>
            <br />
            {{ props.col.description }}
          </q-tooltip>
        </q-th>
      </template>
      <template #body="props">
        <q-tr :props="props">
          <q-td v-for="col in props.cols" :key="col.name" :props="props">
            <div v-if="col.name === 'bed'" :class="`hd-${props.row.bed}-bed-bg hd-badge`">
              {{ col.value }}
            </div>
            <template v-else-if="col.name === 'floorplan_name'">
              {{ col.value }}
              <q-btn
                v-if="floorplanAmenitiesAndTags[col.value] && !printingMode"
                color="primary"
                flat
                clickable
                dense
                size="sm"
                class="q-ml-xs"
              >
                <q-icon name="more" size="small" />
                <q-icon name="arrow_drop_down" size="small" />
                <q-menu
                  transition-duration="100"
                  @show="$tracker.openedPopup('floorplan_amenities', 'unit_mix')"
                >
                  <div class="text-center text-bold q-pa-sm">
                    {{ props.row.label }} > {{ col.value }} > Amenities
                  </div>
                  <q-separator />
                  <q-markup-table flat dense square>
                    <thead :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
                      <tr class="text-left">
                        <th scope="col" class="text-left"></th>
                        <th scope="col" class="text-right">Count</th>
                        <th scope="col" class="text-right">Frequency</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-if="floorplanAmenitiesAndTags[col.value]?.amenities?.length">
                        <tr class="text-bold" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-2'">
                          <td colspan="3" class="text-left q-pa-sm">Advertised Unit Amenities</td>
                          <q-tooltip>
                            Captured from the leasing page of each property website.
                          </q-tooltip>
                        </tr>
                        <tr
                          v-for="(row, index) in floorplanAmenitiesAndTags[col.value].amenities"
                          :key="`amenity-${index}`"
                        >
                          <td class="text-left">
                            {{ row.name }}
                          </td>
                          <td class="text-right">
                            {{ row.count }}
                          </td>
                          <td class="text-right">
                            {{ formatPercent(row.frequency, 0) }}
                          </td>
                        </tr>
                      </template>
                      <template v-if="floorplanAmenitiesAndTags[col.value]?.tags?.length">
                        <tr class="text-bold" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-2'">
                          <td colspan="3" class="text-left q-pa-sm">
                            <q-icon name="auto_awesome" size="14px" class="q-mr-xs text-primary">
                            </q-icon>
                            AI-Detected Unit Amenities
                            <q-tooltip>
                              Detected from floorplan images, floorplan names, and advertised
                              amenities.
                            </q-tooltip>
                          </td>
                        </tr>
                        <tr
                          v-for="(row, index) in floorplanAmenitiesAndTags[col.value].tags"
                          :key="`tag-${index}`"
                        >
                          <td class="text-left">
                            {{ getAmenityTitle(row.name) }}
                          </td>
                          <td class="text-right">
                            {{ row.count }}
                          </td>
                          <td class="text-right">
                            {{ formatPercent(row.frequency, 0) }}
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </q-markup-table>
                </q-menu>
              </q-btn>
            </template>
            <template v-else-if="col.name === Column.TREND">
              <chart-sparkline :values="col.value" label="Effective Rent" />
            </template>
            <template v-else-if="col.name === Column.RENEWAL_PERCENT">
              {{ col.value ? formatPercent(col.value.percentage, 1) : '-' }}
              <q-tooltip max-width="250px">
                {{
                  col.value
                    ? `Estimated percentage of leases that we have seen in the trailing 12 months. Only ${col.value.validLeases - col.value.renewedUnits} of the units were marketed, implying ${col.value.renewedUnits} renewed their leases.`
                    : 'We have not seen enough leases to estimate the renewal rate.'
                }}
              </q-tooltip>
            </template>
            <template v-else>
              <q-btn
                v-if="col.name === Column.LABEL && !printingMode"
                class="q-mr-sm"
                size="xs"
                flat
                dense
                :icon="props.expand ? 'expand_less' : 'expand_more'"
                @click="
                  () => {
                    if (!props.expand) {
                      $tracker.openedPopup('unit_mix')
                    }
                    props.expand = !props.expand
                  }
                "
              />
              {{ col.value }}
            </template>
          </q-td>
        </q-tr>
        <template v-if="props.expand">
          <q-tr
            v-for="unit in props.row.units"
            :key="unit.id"
            :props="props"
            :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
          >
            <template v-for="col in columns" :key="col.name">
              <q-td :props="props" :class="{ 'text-center': col.align === 'center' }">
                <template v-if="col.name === Column.LABEL">
                  <span class="q-pl-xl">{{ props.row.label }}</span>
                </template>
                <template v-else-if="col.name === 'bed'">
                  <div :class="`hd-${props.row.bed}-bed-bg hd-badge`">
                    {{ props.row.bed }}/{{ props.row.bath + (props.row.partial_bath ? 0.5 : 0) }}
                  </div>
                </template>
                <template v-else-if="col.name === 'floorplan_name'">
                  {{
                    unit.floorplan_name ||
                    `${unit.bed} BR/${unit.bath}${unit.partial_bath ? '.5' : ''}BA (${formatNumber(unit.sqft || unit.min_sqft)} sf)`
                  }}
                  <template v-if="unit.unit_name">
                    -
                    <a
                      href="#"
                      class="text-primary"
                      @click="openPriceHistory(props.row.property_id, unit.id)"
                    >
                      Unit {{ unit.unit_name }}
                    </a>
                    <q-btn
                      v-if="unit.amenities.length"
                      color="primary"
                      flat
                      clickable
                      dense
                      size="sm"
                      class="q-ml-xs"
                    >
                      <q-icon name="more" size="small" />
                      <q-icon name="arrow_drop_down" size="small" />
                      <q-menu
                        transition-duration="100"
                        @show="$tracker.openedPopup('unit_amenities', 'unit_mix')"
                      >
                        <div class="text-center text-bold q-pa-sm">
                          {{ props.row.label }} > {{ props.row.floorplan_name }} >
                          {{ unit.unit_name }} > Amenities
                        </div>
                        <q-separator />
                        <q-list dense padding>
                          <q-item
                            v-for="(amenity, index) in unit.amenities"
                            :key="index"
                            v-ripple="false"
                          >
                            <q-item-section> {{ amenity }} </q-item-section>
                          </q-item>
                        </q-list>
                      </q-menu>
                    </q-btn>
                  </template>
                </template>
                <template v-else-if="col.name === Column.DAYS_ON_MARKET">
                  {{ daysOnMarket(unit, new Date()) }}
                </template>
                <template v-else-if="col.name === Column.NUMBER_APPS_TO_CANCELS">
                  {{ numApplications(unit.availability_periods.slice(-1)[0]) }} /
                  {{ numDenials(unit.availability_periods.slice(-1)[0]) }}
                </template>
                <template v-else-if="col.name === Column.CANCEL_PERCENT">
                  {{
                    numApplications(unit.availability_periods.slice(-1)[0])
                      ? formatPercent(
                          numDenials(unit.availability_periods.slice(-1)[0]) /
                            numApplications(unit.availability_periods.slice(-1)[0])
                        )
                      : ''
                  }}
                </template>
                <template v-else-if="col.name === Column.LEASED_DATE">
                  <q-icon v-if="!unit.exit_market" name="info_outline">
                    <q-tooltip max-width="250px">
                      This listing is still on the market. It has been active for
                      <b>{{ daysOnMarket(unit, new Date()) }} days</b>.
                    </q-tooltip>
                  </q-icon>
                  {{ unit.exit_market ? format(parseISO(unit.exit_market), 'M/d/yyyy') : 'Active' }}
                </template>
                <template v-else-if="col.name === Column.SQFT">
                  {{ formatNumber(unit.sqft || unit.min_sqft) }}
                </template>
                <template v-else-if="col.name === Column.MARKET_RENT">
                  {{
                    unit.price
                      ? formatMoney(unit.price)
                      : `${formatMoney(unit.min_price)} - ${formatMoney(unit.max_price)}`
                  }}
                </template>
                <template v-else-if="col.name === Column.RENT_PSF">
                  {{ formatMoney(getPsf(unit), 2) }}
                </template>
                <template v-else-if="col.name === Column.EFFECTIVE_RENT">
                  {{
                    unit.effective_price
                      ? formatMoney(unit.effective_price)
                      : `${formatMoney(unit.min_effective_price)} - ${formatMoney(unit.max_effective_price)}`
                  }}
                </template>
                <template v-else-if="col.name === Column.EFFECTIVE_RENT_PSF">
                  {{ formatMoney(getEffPsf(unit), 2) }}
                </template>
                <template v-else-if="col.name === Column.TRADEOUT_PERCENT">
                  {{ formatPercent(leaseTradeoutFunction(unit), 2, true) }}
                </template>
                <template v-else-if="col.name === Column.TREND">
                  <chart-sparkline :values="computeTrend(unit)" label="Effective Rent" />
                </template>
              </q-td>
            </template>
          </q-tr>
        </template>
        <q-tr
          v-if="props.rowIndex === rows.length - 1"
          class="text-bold text-center title-row"
          :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
        >
          <q-td colspan="3" class="text-left">
            <div>
              Totals/Averages
              <q-icon name="info_outline" v-if="!printingMode">
                <q-tooltip>Computed with underlying units.</q-tooltip>
              </q-icon>
            </div>
          </q-td>
          <q-td v-for="(val, i) in footerRow" :key="i" class="text-center">
            {{ val }}
          </q-td>
        </q-tr>
      </template>
    </q-table>
    <q-dialog v-model="displayUnitLevel" transition-duration="100">
      <unit-level-card
        v-if="selectedUnit"
        :selected-unit="selectedUnit"
        :expanded-unit-history="expandedUnitHistory"
        @close="displayUnitLevel = false"
      />
    </q-dialog>
  </property-report-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { type QTableColumn, exportFile } from 'quasar'
import { addMonths, eachDayOfInterval, format, parseISO } from 'date-fns'
import {
  formatPercent,
  formatNumber,
  formatMoney,
  avg,
  sum,
  unitTypeColor,
  filterColumns,
  sortColumns,
  getTrendDiffPerc,
  getFloorplanAmenitiesAndTags
} from '@/utils'
import {
  type PropertyDetailsResponse,
  type Availability,
  type Price,
  type AvailabilityPeriod,
  daysOnMarket,
  getEffPsf,
  getPsf,
  avgPsf,
  leaseTradeoutOp,
  numApplications,
  numDenials,
  isVacant,
  estimateUnitMix,
  estimatedRenewalsNew,
  sanitizeFieldForCSV,
  getAmenityTitle,
  Column
} from 'common'
import SelectOptions from '@/components/SelectOptions.vue'
import ChartSparkline from '@/components/Charts/ChartSparkline.vue'
import UnitLevelCard from './UnitLevelCard.vue'
import type { ColumnData } from '@/components/PropertyReports/PropertyCompare.vue'
import PropertyReportCard from '@/components/PropertyReportCard.vue'

export default {
  name: 'PropertyCompareEstimatedRentRoll',
  components: { PropertyReportCard, SelectOptions, ChartSparkline, UnitLevelCard },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    details: {
      type: Array<PropertyDetailsResponse>,
      required: true
    },
    selectedBuildings: {
      type: Array as PropType<{ label: string; value: string }[]>,
      required: true
    },
    expandedUnitHistory: {
      type: Object as PropType<{
        [unit_id: string]: {
          created_on: string
          history: Price
        }[]
      }>,
      required: true
    },
    visibleColumns: {
      type: Array as PropType<Column[]>,
      required: true
    },
    toggleableColumns: {
      type: Object as PropType<ColumnData>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      Column,
      numApplications,
      numDenials,
      formatPercent,
      groupBySelection: 'Floorplan' as 'Type' | 'Floorplan' | 'Bed/Bath/SF',
      unitTypeColor,
      format,
      parseISO,
      formatNumber,
      formatMoney,
      daysOnMarket,
      getPsf,
      getEffPsf,
      displayUnitLevel: false,
      selectedUnit: null as {
        unit: Availability
        prop: PropertyDetailsResponse
        period: AvailabilityPeriod
      } | null,
      propertySelections: [] as { label: string; value: string }[],
      trendDiffCache: {}
    }
  },
  computed: {
    floorplanAmenitiesAndTags() {
      return getFloorplanAmenitiesAndTags(this.rows)
    },
    buildingOptions() {
      return this.allProperties.map((_) => {
        return {
          label: _.building_name || _.street_address,
          value: _.id
        }
      })
    },
    allProperties() {
      return this.subjectDetail ? [this.subjectDetail].concat(this.details) : this.details
    },
    filteredDetails(): PropertyDetailsResponse[] {
      return this.allProperties.filter(
        (detail) =>
          this.selectedBuildings.length === 0 ||
          this.selectedBuildings.map((x) => x.value).includes(detail.id)
      )
    },
    columns() {
      return [
        { name: Column.LABEL, label: '', field: 'label', align: 'left', sortable: true },
        {
          name: 'bed',
          label: 'Type',
          field: (row) => {
            const { bed, bath, partial_bath } = row
            return `${bed}/${bath}${partial_bath ? '.5' : ''}`
          },
          align: 'left',
          sortable: true,
          description: 'Unit type as Bed / Bath'
        },
        {
          name: 'floorplan_name',
          label: 'Floorplan',
          field: 'floorplan_name',
          align: 'left',
          sortable: true,
          description:
            'The name of the floorplan the unit belongs to. Displays a generic value when grouped by anything besides floorplan.'
        },
        {
          name: Column.NUMBER_LISTINGS,
          label: '# Listings',
          field: 'number_listings',
          align: 'center',
          sortable: true,
          description: this.toggleableColumns[Column.NUMBER_LISTINGS].description
        },
        {
          name: Column.NUMBER_UNITS,
          label: '# Units',
          field: 'estimated_total_units',
          align: 'center',
          format: (val) => formatNumber(val),
          sortable: true,
          description: this.toggleableColumns[Column.NUMBER_UNITS].description
        },
        {
          name: Column.ESTIMATED_OCCUPANCY,
          label: 'Leased %',
          field: 'estimated_occupancy',
          align: 'center',
          format: (val) => formatPercent(val),
          sortable: true,
          description: this.toggleableColumns[Column.ESTIMATED_OCCUPANCY].description
        },
        {
          name: Column.EXPOSURE_PERCENT,
          label: 'Exposure',
          field: 'exposure_percent',
          align: 'center',
          format: (val) => formatPercent(val),
          sortable: true,
          description: this.toggleableColumns[Column.EXPOSURE_PERCENT].description
        },
        {
          name: Column.RENEWAL_PERCENT,
          label: 'Renewal %',
          field: 'estimated_renewal',
          align: 'center',
          sortable: true,
          description: this.toggleableColumns[Column.RENEWAL_PERCENT].description
        },
        {
          name: Column.DAYS_ON_MARKET,
          label: 'Days on Mkt',
          field: 'days_on_market',
          align: 'center',
          format: (val) => formatNumber(val),
          sortable: true,
          description: this.toggleableColumns[Column.DAYS_ON_MARKET].description
        },
        {
          name: Column.NUMBER_APPS_TO_CANCELS,
          label: '# Apps / Cancels',
          field: 'number_apps',
          align: 'center',
          format: (_, row) =>
            `${formatNumber(row.number_apps)} / ${formatNumber(row.number_cancels)}`,
          sortable: true,
          description: this.toggleableColumns[Column.NUMBER_APPS_TO_CANCELS].description
        },
        {
          name: Column.CANCEL_PERCENT,
          label: 'Cancel % ',
          field: 'cancel_percent',
          align: 'center',
          format: (val) => (val ? formatPercent(val) : ''),
          sortable: true,
          description: this.toggleableColumns[Column.CANCEL_PERCENT].description
        },
        {
          name: Column.LEASED_DATE,
          label: 'Leased',
          field: 'leased_date',
          align: 'center',
          sortable: true,
          description: this.toggleableColumns[Column.LEASED_DATE].description
        },
        {
          name: Column.SQFT,
          label: 'SF',
          field: 'sqft',
          align: 'center',
          format: (val) => formatNumber(val),
          sortable: true,
          description: this.toggleableColumns[Column.SQFT].description
        },
        {
          name: Column.MARKET_RENT,
          label: 'Rent',
          field: 'price',
          align: 'center',
          format: (val) => formatMoney(val, 0),
          sortable: true,
          description: this.toggleableColumns[Column.MARKET_RENT].description
        },
        {
          name: Column.RENT_PSF,
          label: 'PSF',
          field: 'price_psf',
          align: 'center',
          format: (val) => formatMoney(val, 2),
          sortable: true,
          description: this.toggleableColumns[Column.RENT_PSF].description
        },
        {
          name: Column.EFFECTIVE_RENT,
          label: 'NER',
          field: 'effective_price',
          align: 'center',
          format: (val) => formatMoney(val, 0),
          sortable: true,
          description: this.toggleableColumns[Column.EFFECTIVE_RENT].description
        },
        {
          name: Column.EFFECTIVE_RENT_PSF,
          label: 'Effective PSF',
          field: 'effective_price_psf',
          align: 'center',
          format: (val) => formatMoney(val, 2),
          sortable: true,
          description: this.toggleableColumns[Column.EFFECTIVE_RENT_PSF].description
        },
        {
          name: Column.TRADEOUT_PERCENT,
          label: 'Tradeout',
          field: 'lease_tradeout',
          align: 'center',
          format: (val) => formatPercent(val, 2, true),
          sortable: true,
          description: this.toggleableColumns[Column.TRADEOUT_PERCENT].description
        },
        {
          name: Column.TREND,
          label: 'Trend',
          field: 'trend',
          align: 'center',
          sortable: true,
          sort: (
            a: { value: number | null; date: string }[],
            b: { value: number | null; date: string }[],
            rowA: { id: string },
            rowB: { id: string }
          ) => {
            let aDiff, bDiff
            if (this.trendDiffCache[rowA.id] !== undefined) {
              aDiff = this.trendDiffCache[rowA.id]
            } else {
              aDiff = this.getTrendDiffPerc(a)
              this.trendDiffCache[rowA.id] = aDiff
            }
            if (this.trendDiffCache[rowB.id] !== undefined) {
              bDiff = this.trendDiffCache[rowB.id]
            } else {
              bDiff = this.getTrendDiffPerc(b)
              this.trendDiffCache[rowB.id] = bDiff
            }
            return (aDiff || 0) - (bDiff || 0)
          },
          description: this.toggleableColumns[Column.TREND].description
        }
      ]
        .filter((column) => {
          if (column) {
            return filterColumns(column.name, this.toggleableColumns, this.visibleColumns)
          }
          return false
        })
        .sort((columnA, columnB) => {
          return sortColumns(columnA.name, columnB.name, this.columnOrder)
        }) as QTableColumn[]
    },
    columnOrder(): Column[] {
      return [Column.LABEL, Column.BED, Column.FLOORPLAN_NAME, ...this.visibleColumns]
    },
    intervalsStr(): string[] {
      const endDate = new Date()
      const startDate = new Date(2023, 5, 17)
      return eachDayOfInterval({ start: startDate, end: endDate }).map((date) =>
        format(date, 'yyyy-MM-dd')
      )
    },
    rows() {
      const props = this.filteredDetails.filter((_) =>
        this.propertySelections.some(
          (selection) => selection.label === (_.building_name || _.street_address)
        )
      )
      if (!props.length) {
        return []
      }

      return props.flatMap((prop) => {
        const leaseUp = prop.is_lease_up

        const groupBy =
          this.groupBySelection === 'Floorplan'
            ? 'floorplan'
            : this.groupBySelection === 'Bed/Bath/SF'
              ? 'bed_bath_sf'
              : 'bed_bath_half'
        const unitMix = estimateUnitMix(prop, groupBy)

        // Creating rows from grouped units
        const result = []
        for (const unitGroup of unitMix) {
          const units = unitGroup.units
          units.sort((a, b) => {
            const x = (a.sqft || '').toString() + (a.unit_name || '')
            const y = (b.sqft || '').toString() + (b.unit_name || '')
            return x.localeCompare(y)
          })

          const key = unitGroup.key

          const { bed, bath, partial_bath } = units[0]

          const tradeoutValues = []
          const floorplanNamesSet = new Set<string>()
          const sqftTextsSet = new Set<string>()
          let availableUnits = 0
          let vacantUnits = 0

          // create a dictionary of prices per day in intervalStr
          const pricesPerDayGroup: { [key: string]: number[] } = {}
          for (const interval of this.intervalsStr) {
            pricesPerDayGroup[interval] = []
          }

          for (const unit of units) {
            for (const { created_on, history } of this.expandedUnitHistory[unit.id]) {
              if (history.effective_price && created_on in pricesPerDayGroup) {
                pricesPerDayGroup[created_on].push(history.effective_price)
              }
            }

            tradeoutValues.push(this.leaseTradeoutFunction(unit))
            if (unit.floorplan_name) {
              floorplanNamesSet.add(unit.floorplan_name)
            }
            sqftTextsSet.add(`(${formatNumber(unit.sqft || unit.min_sqft)} sf)`)
            if (!unit.exit_market) {
              availableUnits++
            }
            if (isVacant(unit, leaseUp, new Date().toISOString())) {
              vacantUnits++
            }
          }

          const estimatedTotalUnits = unitGroup.estimatedUnits
          const exposurePercentVal = estimatedTotalUnits
            ? availableUnits / estimatedTotalUnits
            : null
          const exposurePercent =
            exposurePercentVal && exposurePercentVal > 1 ? null : exposurePercentVal
          const est_occ = estimatedTotalUnits
            ? (estimatedTotalUnits - vacantUnits) / estimatedTotalUnits
            : -1

          // compute the average price per day for the group
          const pricesPerDayGroupAvg: { value: number | null; date: string }[] = []
          for (const interval of this.intervalsStr) {
            const prices = pricesPerDayGroup[interval]
            pricesPerDayGroupAvg.push({
              value: avg(prices),
              date: interval
            })
          }

          let floorplanName = `${bed}BR / ${bath}${partial_bath ? '.5' : ''}BA${sqftTextsSet.size === 1 ? ' ' + Array.from(sqftTextsSet)[0] : ''}`
          if (this.groupBySelection === 'Floorplan' && floorplanNamesSet.size) {
            floorplanName = Array.from(floorplanNamesSet)[0]
          } else if (this.groupBySelection === 'Type') {
            floorplanName = `${bed}BR / ${bath}${partial_bath ? '.5' : ''}BA${sqftTextsSet.size === 0 ? ' ' + Array.from(sqftTextsSet)[0] : ''}`
          }

          const estRenewal = estimatedTotalUnits
            ? estimatedRenewalsNew(units, estimatedTotalUnits)
            : null

          const lastAvailPeriods = units.flatMap((_) => _.availability_periods.slice(-1))
          const numAppsValue = sum(lastAvailPeriods.map(numApplications))
          const numCancelsValue = sum(lastAvailPeriods.map(numDenials))
          result.push({
            id: prop.id + key,
            property_id: prop.id,
            label: prop.building_name || prop.street_address,
            bed,
            bath: bath ? bath + (partial_bath ? 0.5 : 0) : 0,
            floorplan_name: floorplanName,
            number_listings: units.length,
            exposure_percent: exposurePercent,
            estimated_renewal: estRenewal,
            estimated_occupancy: est_occ > 0 ? est_occ : null,
            estimated_total_units: estimatedTotalUnits,
            days_on_market: avg(units.map((_) => daysOnMarket(_, new Date()))),
            number_apps: numAppsValue,
            number_cancels: numCancelsValue,
            cancel_percent: numCancelsValue / numAppsValue || 0,
            leased_date: null,
            sqft: unitGroup.avgSqft,
            price: unitGroup.avgAskingRent,
            price_psf: unitGroup.avgAskingPsf,
            effective_price: unitGroup.avgEffectiveRent,
            effective_price_psf: unitGroup.avgEffectivePsf,
            lease_tradeout: avg(tradeoutValues),
            trend: pricesPerDayGroupAvg,
            units
          })
        }

        return result.sort(
          (a, b) =>
            (a.bed || -1) - (b.bed || -1) ||
            (a.bath || -1) - (b.bath || -1) ||
            (a.floorplan_name || '').localeCompare(b.floorplan_name || '')
        )
      })
    },
    leaseTradeoutFunction(): (unit: Availability) => number | null {
      const leaseTradeoutMaxDate = new Date()
      const leaseTradeoutMinDate = addMonths(leaseTradeoutMaxDate, -6)

      const leaseTradeoutMaxDateStr = format(leaseTradeoutMaxDate, 'yyyy-MM-dd')
      const leaseTradeoutMinDateStr = format(leaseTradeoutMinDate, 'yyyy-MM-dd')

      return (unit: Availability) => {
        return leaseTradeoutOp(
          unit,
          leaseTradeoutMinDateStr,
          leaseTradeoutMaxDateStr,
          (period) => {
            return period.price
          },
          avg
        )
      }
    },
    footerRow(): (string | null)[] {
      const numberCancels = [] as number[]
      const footerData = {
        number_listings: [],
        estimated_total_units: [],
        exposure_percent: [],
        estimated_renewal: [],
        estimated_occupancy: [],
        days_on_market: [],
        number_apps: [],
        cancel_percent: [],
        leased_date: [],
        sqft: [],
        price: [],
        price_psf: [],
        effective_price: [],
        effective_price_psf: [],
        lease_tradeout: [],
        trend: []
      } as { [key: string]: (number | null)[] }
      const askPsf: { price: number | null; sqft: number | null }[] = []
      const effPsf: { price: number | null; sqft: number | null }[] = []
      let number_listings = 0
      let estimated_total_units = 0
      for (const row of this.rows) {
        if (row.estimated_total_units) {
          estimated_total_units += row.estimated_total_units
        }
        for (const unit of row.units) {
          number_listings += 1
          const lastAvail = unit.availability_periods.slice(-1)[0]
          footerData.days_on_market.push(daysOnMarket(unit, new Date()))
          footerData.number_apps.push(numApplications(lastAvail))
          numberCancels.push(numDenials(lastAvail))
          const askPrice = unit.price || unit.min_price
          footerData.price.push(askPrice)
          const effPrice = unit.effective_price || unit.min_effective_price
          footerData.effective_price.push(effPrice)
          const sqft = unit.sqft || unit.min_sqft
          footerData.sqft.push(sqft)
          askPsf.push({ price: askPrice, sqft })
          effPsf.push({ price: effPrice, sqft })
        }
      }
      footerData.price_psf = [avgPsf(askPsf)].filter((_) => _)
      footerData.effective_price_psf = [avgPsf(effPsf)].filter((_) => _)

      footerData.number_listings.push(number_listings)
      footerData.estimated_total_units.push(estimated_total_units)

      const footerColumnValues = [
        {
          field: Column.NUMBER_LISTINGS,
          value: formatNumber(avg(footerData.number_listings.filter((_) => _) as number[]))
        },
        {
          field: Column.NUMBER_UNITS,
          value: formatNumber(avg(footerData.estimated_total_units.filter((_) => _) as number[]))
        },
        {
          field: Column.ESTIMATED_OCCUPANCY,
          value: null
        },
        {
          field: Column.EXPOSURE_PERCENT,
          value: null
        },
        {
          field: Column.RENEWAL_PERCENT,
          value: null
        },
        {
          field: Column.DAYS_ON_MARKET,
          value: formatNumber(avg(footerData.days_on_market.filter((_) => _) as number[]))
        },
        {
          field: Column.NUMBER_APPS_TO_CANCELS,
          value: `${formatNumber(sum(footerData.number_apps))} / ${formatNumber(sum(numberCancels))}`
        },
        {
          field: Column.CANCEL_PERCENT,
          value: formatPercent(sum(numberCancels) / sum(footerData.number_apps))
        },
        {
          field: Column.LEASED_DATE,
          value: null
        },
        {
          field: Column.SQFT,
          value: formatNumber(avg(footerData.sqft.filter((_) => _) as number[]))
        },
        {
          field: Column.MARKET_RENT,
          value: formatMoney(avg(footerData.price.filter((_) => _) as number[]), 0)
        },
        {
          field: Column.RENT_PSF,
          value: formatMoney(avg(footerData.price_psf.filter((_) => _) as number[]), 2)
        },
        {
          field: Column.EFFECTIVE_RENT,
          value: formatMoney(avg(footerData.effective_price.filter((_) => _) as number[]), 0)
        },
        {
          field: Column.EFFECTIVE_RENT_PSF,
          value: formatMoney(avg(footerData.effective_price_psf.filter((_) => _) as number[]), 2)
        },
        {
          field: Column.TRADEOUT_PERCENT,
          value: null
        },
        {
          field: Column.TREND,
          value: ''
        }
      ]

      return footerColumnValues
        .filter((column) => {
          return filterColumns(column.field, this.toggleableColumns, this.visibleColumns)
        })
        .sort((columnA, columnB) => {
          if (columnA && columnB) {
            return sortColumns(columnA.field, columnB.field, this.visibleColumns)
          }
          return 0
        })
        .map((_) => _.value)
    }
  },
  watch: {
    selectedBuildings() {
      this.updateSelection()
    },
    displayUnitLevel(val: true) {
      if (val) {
        this.$tracker.openedPopup('unit_pricing_history_unit_mix')
      }
    }
  },
  created() {
    this.updateSelection()
  },
  methods: {
    getAmenityTitle,
    getTrendDiffPerc,
    openPriceHistory(propId: string, unitId: string) {
      const prop = this.filteredDetails.find((_) => _.id === propId)
      const unit = prop?.building_availability.find((_) => _.id === unitId)
      if (prop && unit) {
        const today = format(new Date(), 'yyyy-MM-dd')
        const latestToDateObject = unit.availability_periods.reduce((latest, current) => {
          return (current.exit_market || today) > (latest.exit_market || today) ? current : latest
        })
        this.selectedUnit = { unit, prop, period: latestToDateObject }
        this.displayUnitLevel = true
      }
    },
    updateSelection() {
      const vals = this.filteredDetails.filter((_) =>
        this.propertySelections.map((_) => _.label).includes(_.building_name || _.street_address)
      )
      if (!vals.length && this.filteredDetails.length) {
        const { building_name, street_address, id } = this.filteredDetails[0]
        this.propertySelections = [{ label: building_name || street_address, value: id }]
      }
    },
    computeTrend(unit: Availability) {
      const values: Record<string, number | null> = {}

      for (const interval of this.intervalsStr) {
        values[interval] = null
      }

      for (const hist of this.expandedUnitHistory[unit.id]) {
        if (hist.history.effective_price && hist.created_on in values) {
          values[hist.created_on] = hist.history.effective_price
        }
      }

      return this.intervalsStr.map((date) => ({
        value: values[date] || null,
        date
      }))
    },
    exportCSV(exportUnitDetails: boolean = false) {
      const exportDecimal = (num: number | null) => {
        return typeof num === 'number' ? Math.round(num * 100) / 100 : null
      }

      const unitExcludedColumns = [
        {
          name: Column.NUMBER_LISTINGS,
          label: '# Listings',
          valFn: (row: { number_listings: number }) => row.number_listings,
          formatFn: null
        },
        {
          name: Column.NUMBER_UNITS,
          label: '# Units',
          valFn: (row: { estimated_total_units: number | null }) => row.estimated_total_units,
          formatFn: (val: number | null) => (val ? Math.round(val) : null)
        },
        {
          name: Column.ESTIMATED_OCCUPANCY,
          label: 'Leased %',
          valFn: (row: { estimated_occupancy: number | null }) =>
            row.estimated_occupancy ? row.estimated_occupancy * 100 : null,
          formatFn: exportDecimal
        },
        {
          name: Column.EXPOSURE_PERCENT,
          label: 'Exposure',
          valFn: (row: { exposure_percent: number | null }) =>
            row.exposure_percent ? row.exposure_percent * 100 : null,
          formatFn: exportDecimal
        },
        {
          name: Column.RENEWAL_PERCENT,
          label: 'Renewal %',
          valFn: (row: { estimated_renewal: { percentage: number | null } | null }) =>
            row.estimated_renewal?.percentage ? row.estimated_renewal.percentage * 100 : null,
          formatFn: exportDecimal
        }
      ]

      const unitNameColumn = {
        name: 'unit_name',
        label: 'Unit Name',
        valFn: (row: { unit_name: number }) => row.unit_name,
        formatFn: (val: string) => `Unit ${val}`
      }

      const columnConfigs: {
        name: Column | string
        label: string
        valFn: (row: Record<string, any>) => string | number | null
        formatFn: ((val: string | number | null) => string | number | null) | null
      }[] = [
        {
          name: Column.LABEL,
          label: 'Property Name',
          valFn: (row: { label: string }) => row.label,
          formatFn: sanitizeFieldForCSV
        },
        {
          name: 'bedrooms',
          label: 'Bedrooms',
          valFn: (row: { bed: number }) => row.bed,
          formatFn: null
        },
        {
          name: 'bathrooms',
          label: 'Bathrooms',
          valFn: (row: { bath: number }) => row.bath,
          formatFn: (val: number) => val.toFixed(0)
        },
        {
          name: 'partial_bathrooms',
          label: 'Partial Bathrooms',
          valFn: (row: { bath: number }) => (row.bath.toString().includes('.') ? 1 : null),
          formatFn: null
        },
        {
          name: 'floorplan_name',
          label: 'Floorplan',
          valFn: (row: { floorplan_name: string | null }) => row.floorplan_name,
          formatFn: sanitizeFieldForCSV
        },
        {
          name: Column.DAYS_ON_MARKET,
          label: 'Days on Market',
          valFn: (row: { days_on_market: number | null }) =>
            row.days_on_market ? Math.round(row.days_on_market) : null,
          formatFn: (val: number | null) => (val ? Math.round(val) : null)
        },
        {
          name: Column.NUMBER_APPS_TO_CANCELS,
          label: '# Applications',
          valFn: (row: { number_apps: number }) => row.number_apps,
          formatFn: exportDecimal
        },
        {
          name: Column.NUMBER_APPS_TO_CANCELS,
          label: '# Cancellations',
          valFn: (row: { number_cancels: number }) => row.number_cancels,
          formatFn: exportDecimal
        },
        {
          name: Column.CANCEL_PERCENT,
          label: 'Cancel %',
          valFn: (row: { cancel_percent: number }) => row.cancel_percent * 100,
          formatFn: exportDecimal
        },
        {
          name: Column.SQFT,
          label: 'SF',
          valFn: (row: { sqft: number | null }) => row.sqft,
          formatFn: (val: number | null) => (val ? Math.round(val) : null)
        },
        {
          name: Column.MARKET_RENT,
          label: 'Rent',
          valFn: (row: { price: number | null }) => row.price,
          formatFn: (val: number | null) => (val ? Math.round(val) : null)
        },
        {
          name: Column.RENT_PSF,
          label: 'Rent PSF',
          valFn: (row: { price_psf: number | null }) => row.price_psf,
          formatFn: exportDecimal
        },
        {
          name: Column.EFFECTIVE_RENT,
          label: 'NER',
          valFn: (row: { effective_price: number | null }) => row.effective_price,
          formatFn: (val: number | null) => (val ? Math.round(val) : null)
        },
        {
          name: Column.EFFECTIVE_RENT_PSF,
          label: 'NER PSF',
          valFn: (row: { effective_price_psf: number | null }) => row.effective_price_psf,
          formatFn: exportDecimal
        },
        {
          name: Column.TRADEOUT_PERCENT,
          label: 'Tradeout',
          valFn: (row: { lease_tradeout: number | null }) =>
            row.lease_tradeout ? row.lease_tradeout * 100 : null,
          formatFn: exportDecimal
        }
      ]

      if (!exportUnitDetails) {
        columnConfigs.push(...unitExcludedColumns)
      }

      const exportColumns = columnConfigs
        .filter((column) => {
          if (column) {
            return filterColumns(column.name, this.toggleableColumns, this.visibleColumns)
          }
          return false
        })
        .sort((columnA, columnB) => {
          if (columnA && columnB) {
            return sortColumns(columnA.name, columnB.name, this.visibleColumns)
          }
          return 0
        })

      // for the unit details, we insert the unit name column as the second column
      if (exportUnitDetails) {
        exportColumns.splice(1, 0, unitNameColumn)
      }

      const headers = exportColumns.map((col) => col.label)
      const rowsToInclude = exportUnitDetails
        ? this.rows.flatMap((row) =>
            row.units.map((unitRow) => {
              return {
                label: row.label,
                ...unitRow,
                number_apps: numApplications(unitRow.availability_periods.slice(-1)[0]),
                number_cancels: numDenials(unitRow.availability_periods.slice(-1)[0]),
                cancel_percent: numApplications(unitRow.availability_periods.slice(-1)[0])
                  ? numDenials(unitRow.availability_periods.slice(-1)[0]) /
                    numApplications(unitRow.availability_periods.slice(-1)[0])
                  : '',
                bath: unitRow.bath + (unitRow.partial_bath ? 0.5 : 0),
                price_psf: getPsf(unitRow),
                effective_price_psf: getEffPsf(unitRow),
                lease_tradeout: this.leaseTradeoutFunction(unitRow)
              }
            })
          )
        : this.rows

      const rows = rowsToInclude.map((row) =>
        exportColumns.map((col) => {
          const value = col.valFn(row)
          return col.formatFn ? col.formatFn(value) : value
        })
      )

      const today = new Date().toISOString().split('T')[0]
      const fileName = exportUnitDetails
        ? `hello-data-units-${today}.csv`
        : `hello-data-unit-mix-${today}.csv`
      exportFile(fileName, headers.join(',') + '\n' + rows.map((row) => row.join(',')).join('\n'))

      if (exportUnitDetails) {
        this.$tracker.exported('unit_mix_unit_details')
      } else {
        this.$tracker.exported('unit_mix')
      }
    },
    sortingChanged(colName: string) {
      this.$tracker.changedSort('unit_mix', colName)
    }
  }
}
</script>
<style scoped>
:deep(.overflow-select .q-field__native) {
  overflow: hidden;
  white-space: nowrap;
}
.title-row td {
  border-top: 2px solid rgba(0, 0, 0, 0.12);
}
</style>
